import React, { FormEvent, useMemo, useState } from "react";
import {
  forgotPassword,
  forgotUpdatePassword,
} from "../../api/auth";
import LegacyAuthLogo from "../../components/logo/LegacyAuthLogo";
import { pgAuthLogin, pgAuthPasswordReset } from "../../components/mixPanelEvents/mixPanelEvents";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type PgInputProps = {
  id: string;
  name: string;
  type: "password" | "text";
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  autoComplete?: string;
  minLength?: number;
};

const PgInput = ({
  id,
  name,
  type,
  label,
  value,
  onChange,
  className = "",
  autoComplete = "",
  minLength,
}: PgInputProps) => (
  <div className={`pg-input ${type === "password" ? "pg-input-has-eye" : ""} ${className}`.trim()}>
    <input
      id={id}
      type={type}
      name={name}
      value={value}
      required
      autoComplete={autoComplete}
      minLength={minLength}
      onChange={(event) => onChange(event.target.value)}
    />
    <label htmlFor={id} className="pg-input-label">
      {label}
    </label>
    <div className="pg-input-error text-end" />
    {type === "password" && (
      <div className="pg-input-eye">
        <svg width="22" height="15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.09 15c4.823 0 8.574-3.875 10.181-6.125.67-.875.67-2 0-2.875-1.607-2.125-5.358-6-10.18-6C6.268 0 2.518 3.875.91 6.125c-.67.875-.67 2 0 2.75C2.518 11.125 6.268 15 11.09 15zm0-11.25c2.278 0 4.02 1.625 4.02 3.75s-1.742 3.75-4.02 3.75c-2.277 0-4.018-1.625-4.018-3.75s1.741-3.75 4.019-3.75z" fill="#fff"></path>
        </svg>
      </div>
    )}
  </div>
);

const ForgotPasswordPage = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const emailFromLink = query.get("email") || "";
  const keyFromLink = query.get("key") || "";
  const isUpdateVariant = Boolean(emailFromLink && keyFromLink);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [formMessageType, setFormMessageType] = useState<"success" | "error" | null>(null);

  const isRequestValid = EMAIL_REGEX.test(username.trim());
  const isUpdateValid = password.length >= 8 && password2.length >= 8 && password === password2;

  const submitResetRequest = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!isRequestValid) {
      return;
    }

    setLoading(true);
    setFormMessage("");
    setFormMessageType(null);

    try {
      const response = await forgotPassword({ username: username.trim() });
      if (!response.success) {
        setFormMessage(response.message);
        setFormMessageType("error");
        return;
      }

      pgAuthPasswordReset(response.googleUser ? "Google" : "Email");
      setFormMessage(response.message);
      setFormMessageType("success");
    } catch (error) {
      setFormMessage("Something went wrong. Please try again.");
      setFormMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const submitPasswordUpdate = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!isUpdateValid) {
      return;
    }

    setLoading(true);
    setFormMessage("");
    setFormMessageType(null);

    try {
      const response = await forgotUpdatePassword({
        email: emailFromLink,
        key: keyFromLink,
        password,
        password2: password,
      });

      if (!response.success) {
        setFormMessage(response.message);
        setFormMessageType("error");
        return;
      }

      pgAuthPasswordReset();
      pgAuthLogin();
      localStorage.removeItem("hideAppBar");

      window.location.href = `${window.location.origin}/courses`;
    } catch (error) {
      setFormMessage("Something went wrong. Please try again.");
      setFormMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pg-users-forms-screen">
      <div className="pg-users-forms-screen-content">
        <div className="pg-users-forms-screen-sidebar forgot-pass-sidebar">
          <LegacyAuthLogo fill="#fff" href={window.location.origin} />
          <div className="pg-users-forms-screen-sidebar-quote">
            <span className="pg-users-forms-screen-sidebar-quote-huge-text">“The best all-in-one golf training system”</span>
            <span className="pg-users-forms-screen-sidebar-quote-big-text">Martin Chuck</span>
            <span className="pg-users-forms-screen-sidebar-quote-small-text">Golf Digest Top 10 Instructor</span>
          </div>
        </div>
        <div className="pg-users-forms-screen-main-content">
          <div className="pg-users-forms-screen-main-content-wrapper">
            {isUpdateVariant ? (
              <>
                <div className="logo-mobile">
                  <LegacyAuthLogo fill="#000" href={window.location.origin} />
                </div>
                <h1 className="fs-32 fw-700 text-left mb-40">Update Password</h1>
                <form className="d-inline-block w-100" onSubmit={submitPasswordUpdate}>
                  {formMessage && (
                    <div
                      className={`pg-form-error ${formMessageType === "success" ? "pg-form-error-success" : ""}`}
                      dangerouslySetInnerHTML={{ __html: formMessage }}
                    />
                  )}
                  <PgInput
                    id="pg-input-password"
                    name="password"
                    type="password"
                    label="New Password *"
                    value={password}
                    onChange={setPassword}
                    minLength={8}
                  />
                  <PgInput
                    id="pg-input-password2"
                    name="password2"
                    type="password"
                    label="Confirm Password *"
                    value={password2}
                    onChange={setPassword2}
                    minLength={8}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary d-block w-100"
                    disabled={!isUpdateValid || loading}
                  >
                    Update Password
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="logo-mobile">
                  <LegacyAuthLogo fill="#000" />
                </div>
                <h1 className="fs-32 fw-700 text-left mb-40">Forgot Password</h1>
                <form className="w-100" onSubmit={submitResetRequest}>
                  {formMessage && (
                    <div
                      className={`pg-form-error ${formMessageType === "success" ? "pg-form-error-success" : ""}`}
                      dangerouslySetInnerHTML={{ __html: formMessage }}
                    />
                  )}
                  <PgInput
                    id="pg-input-username"
                    name="username"
                    type="text"
                    label="Username / Email"
                    value={username}
                    onChange={setUsername}
                    className="mb-24"
                    autoComplete="username"
                  />
                  <button
                    type="submit"
                    className="btn btn-primary d-block w-100"
                    disabled={!isRequestValid || loading}
                  >
                    Send Verification Email
                  </button>
                  <div className="transparent-btn text-center mb-16">
                    <a href={window.location.origin} className="text-decoration-none fw-800 fs-16">
                      Cancel
                    </a>
                  </div>
                </form>
                <p className="small-text text-center">
                  Need help?{" "}
                  <a href="https://www.performancegolf.com/contact" className="d-inline-block green-link" target="_blank" rel="noreferrer">
                    Contact Us
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
